'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '@/components/ui/carousel'

export type Mode = 'bus' | 'train'

type FareBucketId = 'lastMinute' | 'oneWeek' | 'twoWeeks' | 'monthPlus' | 'twoWeeksPlus'

interface FareBucket {
    id: FareBucketId
    fareUsd: number
}

interface FeaturedRoute {
    id: string
    routeName: string
    fares: Record<Mode, FareBucket[]>
}

/**
 * The curated route set (blog spec OOS-6: static until live fare data lands —
 * see the follow-up issue tracking that). Median one-way fares by
 * booking-lead-time window, per mode, computed from Snowflake
 * MAMMOTH.REPORT.FACT_TRANSACTION (bookings, trailing 6 months) — each
 * transaction normalized to its own route's median first, to avoid the
 * cross-route composition bias a naive network-wide median produces (routes
 * booked mostly last-minute skew a pooled "same-day" figure toward whatever
 * those routes typically cost, regardless of lead time). A geographic mix,
 * not concentrated in one region. Editable in place — swap routes/fares as
 * the curated set is refreshed.
 */
export const FEATURED_ROUTES: FeaturedRoute[] = [
    {
        id: 'boston-new-york',
        routeName: 'Boston ↔ New York',
        fares: {
            bus: [
                { id: 'lastMinute', fareUsd: 45 },
                { id: 'oneWeek', fareUsd: 45 },
                { id: 'twoWeeks', fareUsd: 42 },
                { id: 'monthPlus', fareUsd: 41 },
            ],
            train: [
                { id: 'lastMinute', fareUsd: 94 },
                { id: 'oneWeek', fareUsd: 69 },
                { id: 'twoWeeks', fareUsd: 60 },
                { id: 'monthPlus', fareUsd: 54 },
            ],
        },
    },
    {
        id: 'washington-dc-new-york',
        routeName: 'Washington, DC ↔ New York',
        fares: {
            bus: [
                { id: 'lastMinute', fareUsd: 45 },
                { id: 'oneWeek', fareUsd: 41 },
                { id: 'twoWeeks', fareUsd: 42 },
                { id: 'monthPlus', fareUsd: 41 },
            ],
            train: [
                { id: 'lastMinute', fareUsd: 96 },
                { id: 'oneWeek', fareUsd: 69 },
                { id: 'twoWeeks', fareUsd: 54 },
                { id: 'monthPlus', fareUsd: 54 },
            ],
        },
    },
    {
        id: 'new-york-philadelphia',
        routeName: 'New York ↔ Philadelphia',
        fares: {
            bus: [
                { id: 'lastMinute', fareUsd: 28 },
                { id: 'oneWeek', fareUsd: 25 },
                { id: 'twoWeeks', fareUsd: 25 },
                { id: 'monthPlus', fareUsd: 26 },
            ],
            train: [
                { id: 'lastMinute', fareUsd: 30 },
                { id: 'oneWeek', fareUsd: 27 },
                { id: 'twoWeeks', fareUsd: 26 },
                { id: 'monthPlus', fareUsd: 30 },
            ],
        },
    },
    {
        id: 'seattle-portland',
        routeName: 'Seattle ↔ Portland',
        fares: {
            bus: [
                { id: 'lastMinute', fareUsd: 44 },
                { id: 'oneWeek', fareUsd: 42 },
                { id: 'twoWeeksPlus', fareUsd: 39 },
            ],
            train: [
                { id: 'lastMinute', fareUsd: 55 },
                { id: 'oneWeek', fareUsd: 51 },
                { id: 'twoWeeksPlus', fareUsd: 46 },
            ],
        },
    },
]

// The brand's blue scale (globals.css --wanderblu-*), light to dark. Bars
// shade progressively darker from same-day to the longest lead-time window,
// so the eye reads "further out" as "more toward the deal" at a glance.
const WANDERBLU_STOPS = ['50', '500', '600', '700', '800', '900']

function shadesFor(count: number): string[] {
    if (count === 1) {
        return [WANDERBLU_STOPS[WANDERBLU_STOPS.length - 1]]
    }
    return Array.from({ length: count }, (_, index) => {
        const stopIndex = Math.round((index * (WANDERBLU_STOPS.length - 1)) / (count - 1))
        return WANDERBLU_STOPS[stopIndex]
    })
}

interface FareBarsProps {
    buckets: FareBucket[]
    bucketLabels: Record<FareBucketId, string>
    cheapestBucketId: FareBucketId
}

const FareBars = ({ buckets, bucketLabels, cheapestBucketId }: FareBarsProps) => {
    const maxFareUsd = Math.max(...buckets.map((bucket) => bucket.fareUsd))
    const shades = shadesFor(buckets.length)

    return (
        <div className="flex h-[180px] items-end justify-between gap-3">
            {buckets.map((bucket, index) => (
                <div
                    key={bucket.id}
                    className="flex h-full flex-1 flex-col items-center justify-end gap-2"
                >
                    <span
                        className={
                            bucket.id === cheapestBucketId
                                ? 'text-[13px] font-bold text-primary-hover'
                                : 'text-[13px] font-bold text-text-primary'
                        }
                    >
                        ${bucket.fareUsd}
                    </span>
                    <div
                        className="w-full rounded-t"
                        style={{
                            height: `${(bucket.fareUsd / maxFareUsd) * 100}%`,
                            backgroundColor: `var(--wanderblu-${shades[index]})`,
                        }}
                    />
                    <span className="text-center text-[11px] text-muted-foreground">
                        {bucketLabels[bucket.id]}
                    </span>
                </div>
            ))}
        </div>
    )
}

/**
 * The cheapest booking window ("sweet spot") and what waiting until the
 * cheapest window saves versus booking same-day, derived from the curated
 * fare curve — no separate hardcoded stats to keep in sync with the bars.
 */
function computeFareStats(buckets: FareBucket[]) {
    const lastMinuteFareUsd = buckets[0].fareUsd
    const cheapest = buckets.reduce((min, bucket) => (bucket.fareUsd < min.fareUsd ? bucket : min))
    return {
        sweetSpotBucketId: cheapest.id,
        savingsUsd: Math.max(lastMinuteFareUsd - cheapest.fareUsd, 0),
    }
}

/**
 * The "By the Numbers" featured-data band: a carousel of four curated routes,
 * each showing its median one-way fare across booking-lead-time windows for a
 * selected mode. A mode control switches every slide's fare curve at once.
 */
export const ByTheNumbers = () => {
    const t = useTranslations('blogHome.byTheNumbers')
    const [mode, setMode] = useState<Mode>('bus')

    const bucketLabels: Record<FareBucketId, string> = {
        lastMinute: t('buckets.lastMinute'),
        oneWeek: t('buckets.oneWeek'),
        twoWeeks: t('buckets.twoWeeks'),
        monthPlus: t('buckets.monthPlus'),
        twoWeeksPlus: t('buckets.twoWeeksPlus'),
    }
    const activeModeLabel = mode === 'bus' ? t('busLabel') : t('trainLabel')

    return (
        <section
            aria-label={t('heading')}
            className="overflow-x-clip border-y border-border bg-muted px-4 py-10 md:px-10 md:py-14"
        >
            <div className="mx-auto w-full max-w-[1400px]">
                <div className="mb-2 flex items-center gap-3">
                    <span aria-hidden="true" className="h-[3px] w-6 rounded-full bg-primary" />
                    <span className="text-[13px] font-bold text-text-link">{t('eyebrow')}</span>
                </div>
                <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight text-text-primary md:text-[28px]">
                            {t('heading')}
                        </h2>
                        <p className="mt-2 max-w-xl text-[15px] text-muted-foreground md:text-base">
                            {t('subheading', { mode: activeModeLabel })}
                        </p>
                    </div>
                    <div className="flex gap-1 rounded-full bg-background p-1 shadow-sm">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className={
                                mode === 'bus'
                                    ? 'rounded-full bg-primary-hover text-primary-foreground hover:bg-primary-hover'
                                    : 'rounded-full'
                            }
                            aria-pressed={mode === 'bus'}
                            onClick={() => setMode('bus')}
                        >
                            {t('busLabel')}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className={
                                mode === 'train'
                                    ? 'rounded-full bg-primary-hover text-primary-foreground hover:bg-primary-hover'
                                    : 'rounded-full'
                            }
                            aria-pressed={mode === 'train'}
                            onClick={() => setMode('train')}
                        >
                            {t('trainLabel')}
                        </Button>
                    </div>
                </div>

                <Carousel opts={{ align: 'start' }} className="px-1">
                    <CarouselContent>
                        {FEATURED_ROUTES.map((route) => {
                            const buckets = route.fares[mode]
                            const stats = computeFareStats(buckets)

                            return (
                                <CarouselItem
                                    key={route.id}
                                    data-testid={`by-the-numbers-route-${route.id}`}
                                    className="basis-full"
                                >
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.6fr_1fr]">
                                        <div className="rounded-lg bg-background p-5 shadow-sm">
                                            <h3 className="mb-4 text-lg font-bold text-text-primary">
                                                {route.routeName}
                                            </h3>
                                            <FareBars
                                                buckets={buckets}
                                                bucketLabels={bucketLabels}
                                                cheapestBucketId={stats.sweetSpotBucketId}
                                            />
                                        </div>
                                        <div className="flex flex-col gap-4">
                                            <div className="rounded-lg bg-background p-5 shadow-sm">
                                                <div className="text-3xl font-bold text-primary-hover">
                                                    {bucketLabels[stats.sweetSpotBucketId]}
                                                </div>
                                                <div className="mt-1 text-sm text-muted-foreground">
                                                    {t('sweetSpotCaption')}
                                                </div>
                                            </div>
                                            <div className="rounded-lg bg-background p-5 shadow-sm">
                                                <div className="text-3xl font-bold text-primary-hover">
                                                    ${stats.savingsUsd}
                                                </div>
                                                <div className="mt-1 text-sm text-muted-foreground">
                                                    {t('savingsCaption')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </CarouselItem>
                            )
                        })}
                    </CarouselContent>
                    <CarouselPrevious className="left-2" />
                    <CarouselNext className="right-2" />
                </Carousel>
            </div>
        </section>
    )
}
